import { errorMessage, needsIdempotency, type ErrorPayload } from './api-contract.js';

export type ApiEnvelope<T> = { data: T; requestId: string };
const sessionRequests = new Set<AbortController>();
function cancelSessionRequests(): void { for (const request of sessionRequests) request.abort(); sessionRequests.clear(); }
if (typeof window !== 'undefined') window.addEventListener('storage', (event) => {
  if (event.key === 'after-sales-erp-token' || event.key === null) cancelSessionRequests();
});

export class ApiError extends Error {
  constructor(public readonly status: number, message: string) {
    super(message);
  }
}

export function getToken(): string | null {
  return localStorage.getItem('after-sales-erp-token');
}

export function setToken(token: string | null): void {
  cancelSessionRequests();
  if (token) localStorage.setItem('after-sales-erp-token', token);
  else localStorage.removeItem('after-sales-erp-token');
  window.dispatchEvent(new Event('erp-auth-change'));
}

export type ApiOptions = RequestInit & { idempotencyKey?: string };

export async function api<T>(path: string, options: ApiOptions = {}): Promise<T> {
  const publicRequest = path.startsWith('/public/');
  const token = publicRequest ? null : getToken();
  const { idempotencyKey, ...requestOptions } = options;
  const method = requestOptions.method ?? 'GET';
  const controller = new AbortController();
  const sessionRequest = !publicRequest && !path.startsWith('/auth/');
  if (sessionRequest) sessionRequests.add(controller);
  try {
  const response = await fetch(`/api/v1${path}`, {
    ...requestOptions,
    signal: requestOptions.signal ? AbortSignal.any([requestOptions.signal, controller.signal]) : controller.signal,
    headers: {
      'content-type': 'application/json',
      ...(token ? { authorization: `Bearer ${token}` } : {}),
      ...(needsIdempotency(method, path) ? { 'idempotency-key': idempotencyKey ?? crypto.randomUUID() } : {}),
      ...requestOptions.headers
    }
  });
  const payload = await response.json().catch(() => ({})) as ErrorPayload & { data?: T };
  // A response from a departed session must not populate a new session or log it out.
  if (!publicRequest && !path.startsWith('/auth/') && token !== getToken()) {
    throw new DOMException('账号已切换，请重新查询', 'AbortError');
  }
  if (!response.ok) {
    if (response.status === 401 && token && token === getToken()) setToken(null);
    throw new ApiError(response.status, errorMessage(payload, response.status));
  }
  if (!Object.prototype.hasOwnProperty.call(payload, 'data') || payload.data === undefined) {
    throw new ApiError(response.status, '接口返回的数据不完整，请刷新后重试');
  }
  return payload.data as T;
  } finally { sessionRequests.delete(controller); }
}
